/**
 * 状态加载包装器 - 解决状态闪烁问题
 *
 * 🎯 核心功能：
 * 1. 在状态未初始化时显示加载状态
 * 2. 避免显示错误的默认状态
 * 3. 提供一致的加载体验
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionStore } from '@/stores/subscription-store';

interface StateLoadingWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoadingText?: boolean;
  className?: string;
}

/**
 * 状态加载包装器组件
 * 
 * 使用方式：
 * ```tsx
 * <StateLoadingWrapper>
 *   <SubscriptionStatusBadge />
 * </StateLoadingWrapper>
 * ```
 */
export const StateLoadingWrapper: React.FC<StateLoadingWrapperProps> = ({
  children,
  fallback,
  showLoadingText = true,
  className = ''
}) => {
  const { t } = useTranslation();
  const { loading: isLoading } = useAuth();
  const isInitialized = true; // 简化状态管理

  // 如果状态未初始化或正在加载，显示加载状态
  if (!isInitialized || isLoading) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
        {showLoadingText && (
          <span className="text-sm text-muted-foreground">{t('app.common.loading')}</span>
        )}
      </div>
    );
  }

  // 状态已初始化，显示子组件
  return <>{children}</>;
};

/**
 * 订阅状态加载包装器 - 专门用于订阅相关组件
 * 🔧 2025-01 重构: 增强数据就绪检查，防止闪烁
 */
export const SubscriptionStateWrapper: React.FC<{
  children: React.ReactNode;
  className?: string;
  /** 是否显示骨架屏而非加载提示 */
  useSkeleton?: boolean;
}> = ({ children, className = '', useSkeleton = false }) => {
  const { initialLoading, status } = useSubscriptionStore();

  // 🔧 关键修复: 等待数据真正就绪
  // 不仅检查initialLoading，还要确保status已加载
  const isDataReady = !initialLoading && status !== null;

  if (!isDataReady) {
    if (useSkeleton) {
      // 骨架屏模式
      return (
        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-muted animate-pulse ${className}`}>
          <div className="h-3 w-3 rounded-full bg-gray-300" />
          <div className="h-3 w-16 bg-gray-300 rounded" />
        </div>
      );
    } else {
      // 加载提示模式
      return (
        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-muted ${className}`}>
          <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400"></div>
          <span className="text-muted-foreground">检查订阅状态...</span>
        </div>
      );
    }
  }

  return <>{children}</>;
};

/**
 * 使用次数状态加载包装器 - 专门用于使用次数相关组件
 */
export const UsageStateWrapper: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <StateLoadingWrapper
      fallback={
        <div className={`inline-flex items-center space-x-1 text-sm ${className}`}>
          <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400"></div>
          <span className="text-muted-foreground">检查使用次数...</span>
        </div>
      }
    >
      {children}
    </StateLoadingWrapper>
  );
};

/**
 * 用户状态加载包装器 - 专门用于用户信息相关组件
 */
export const UserStateWrapper: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <StateLoadingWrapper
      fallback={
        <div className={`inline-flex items-center space-x-1 ${className}`}>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          <span className="text-sm text-muted-foreground">加载用户信息...</span>
        </div>
      }
    >
      {children}
    </StateLoadingWrapper>
  );
};
